/**
 * English catalog — SOURCE OF TRUTH for every UI string.
 * `MessageKey` / `Messages` are derived from this object; the other six
 * locale files implement `Messages`, so a missing or extra key anywhere
 * is a compile error (this is the completeness gate).
 *
 * Interpolation: `{name}` placeholders, filled by `t(key, { name })`.
 * Pluralization: keys ending in `.one/.few/.many/.other` are picked by
 * the `plural(base, count)` helper via Intl.PluralRules.
 */
export const en = {
  // ── nav ────────────────────────────────────────────────────────────────
  'nav.timeline': 'Timeline',
  'nav.search': 'Search',
  'nav.duplicates': 'Duplicates',
  'nav.collections': 'Collections',
  'nav.import': 'Import',
  'nav.trash': 'Trash',
  'nav.settings': 'Settings',

  // ── common actions ─────────────────────────────────────────────────────
  'common.restore': 'Restore',
  'common.delete': 'Delete',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.close': 'Close',
  'common.upgrade': 'Upgrade',
  'common.upgradeToPro': 'Upgrade to Pro',
  'common.search': 'Search',
  'common.tryAgain': 'Try again',
  'common.loading': 'Loading…',
  'common.back': 'Back',
  'common.skip': 'Skip',
  'common.verify': 'Verify',
  'common.resend': 'Resend',
  'common.free': 'Free',
  'common.pro': 'Pro',
  'common.clearFilters': 'Clear filters',
  'common.addToChrome': 'Add to Chrome',
  'toast.genericError': 'Something went wrong. Please try again.',

  // ── plural units (picked by plural(base, count)) ───────────────────────
  'unit.tabs.one': '{count} tab',
  'unit.tabs.few': '{count} tabs',
  'unit.tabs.many': '{count} tabs',
  'unit.tabs.other': '{count} tabs',
  'unit.sessions.one': '{count} session',
  'unit.sessions.few': '{count} sessions',
  'unit.sessions.many': '{count} sessions',
  'unit.sessions.other': '{count} sessions',
  'unit.items.one': '{count} item',
  'unit.items.few': '{count} items',
  'unit.items.many': '{count} items',
  'unit.items.other': '{count} items',
  'unit.entries.one': '{count} entry',
  'unit.entries.few': '{count} entries',
  'unit.entries.many': '{count} entries',
  'unit.entries.other': '{count} entries',
  'unit.copies.one': '{count} copy',
  'unit.copies.few': '{count} copies',
  'unit.copies.many': '{count} copies',
  'unit.copies.other': '{count} copies',
  'unit.browsers.one': '{count} browser',
  'unit.browsers.few': '{count} browsers',
  'unit.browsers.many': '{count} browsers',
  'unit.browsers.other': '{count} browsers',

  // ── app shell ──────────────────────────────────────────────────────────
  'shell.searchPlaceholder': 'Search your vault…',
  'shell.saved': 'SAVED',
  'shell.toggleTheme': 'Toggle theme',

  // ── timeline ───────────────────────────────────────────────────────────
  'timeline.empty.title': 'Your vault is empty — for now',
  'timeline.empty.body':
    'Sweep a window with the Nomad Tabs extension, or rescue your old OneTab graveyard. Everything lands here, searchable.',
  'timeline.empty.import': 'Import your tabs',
  'timeline.empty.importOneTab': 'Import from OneTab',
  'timeline.empty.tip': 'TIP: ⇧⌘S SWEEPS THE CURRENT WINDOW FROM THE EXTENSION',
  'timeline.noMatch.title': 'No sessions match these filters',
  'timeline.noMatch.body': 'Try a wider date range, another browser, or turn off “only with content”.',
  'timeline.loadOlder': 'Load older sessions',
  'timeline.toast.sessionDeleted': 'Session deleted',
  'timeline.toast.loadFailed': 'Could not load that session',
  'timeline.delete.title': 'Delete this session?',
  'timeline.delete.subtitle': 'Removes “{title}” and its {tabs} saved. This can’t be undone.',
  'timeline.delete.body': 'You can still restore its tabs from the browser history if you change your mind.',
  'timeline.delete.confirm': 'Delete session',
  'timeline.delete.fallbackTitle': 'this session',
  'timeline.offline.title': 'You’re offline',
  'timeline.offline.body': 'Your vault works locally — sync resumes on its own when you’re back online.',
  'timeline.error.title': 'Couldn’t load your timeline',
  'timeline.error.body': 'Something went wrong reaching the vault. Check your connection and try again.',

  // ── search ─────────────────────────────────────────────────────────────
  'search.mode.text': 'Text',
  'search.mode.hybrid': 'Hybrid',
  'search.mode.semantic': 'Semantic',
  'search.placeholder': 'Search your vault…',
  'search.clear': 'Clear search',
  'search.status.counted': 'SEARCHING {count} TABS',
  'search.status.default': 'SEARCHING YOUR VAULT',
  'search.empty.title': 'Search everything you’ve saved',
  'search.empty.body':
    'Find a tab by its title, its address, or the words on the page. Semantic mode finds it by meaning.',
  'search.noMatch.title': 'No matches for “{q}”',
  'search.noMatch.body': 'Try fewer or different words, switch to Hybrid mode, or clear your filters.',
  'search.failed.eyebrow': '— SEARCH FAILED',
  'search.failed.body': 'Something went wrong reaching your vault.',
  'search.failed.retryHint': 'Give it another try.',
  'search.results': '{count} RESULTS',
  'search.sort.relevance': 'Relevance',
  'search.sort.date': 'Date',
  'search.loadMore': 'Load more results',
  'search.bestMatch': 'BEST MATCH',
  'search.openedIn': 'YOU OPENED THIS IN {browser}',
  'search.facet.browser': 'BROWSER',
  'search.facet.date': 'DATE',
  'search.facet.topDomains': 'TOP DOMAINS',
  'search.range.any': 'Any time',
  'search.range.24h': '24 hours',
  'search.range.7d': '7 days',
  'search.range.30d': '30 days',
  'search.range.1y': '12 months',
  'search.upsell.eyebrow': 'SEMANTIC SEARCH · PRO',
  'search.upsell.title': 'Semantic search is a Pro feature',
  'search.upsell.body':
    'Find tabs by meaning, not just keywords — “that article about central bank rate cuts” instead of the exact title. Upgrade to search across everything you’ve ever saved.',
  'search.upsell.seePlans': 'See Pro plans',
  'search.upsell.useText': 'Search with text instead',

  // ── duplicates ─────────────────────────────────────────────────────────
  'dupes.title': 'Duplicates center',
  'dupes.summary.duplicates': '{count} DUPLICATES',
  'dupes.summary.ram': '≈ {ram} RAM TO FREE',
  'dupes.closeSelected': 'Close selected ({count})',
  'dupes.keepNewest': 'Keep newest, close rest',
  'dupes.error.title': 'Couldn’t load your duplicates',
  'dupes.toast.closed': 'Closed {copies}',
  'dupes.toast.closedBody': 'The newest copy stays open — every copy is still saved in your vault.',

  // ── collections ────────────────────────────────────────────────────────
  'collections.title': 'Collections',
  'collections.subtitle': 'Auto-sorted by AI — rename or merge anytime',
  'collections.new': 'New collection',
  'collections.banner.title': 'AI auto-collections are a Pro feature.',
  'collections.banner.body': 'You can still create and organize collections by hand — that’s free, forever.',
  'collections.empty.title': 'No collections yet',
  'collections.empty.body':
    'Group related tabs by topic. Create one by hand — Pro also sorts your vault into collections automatically.',
  'collections.error.title': 'Couldn’t load your collections',

  // ── import ─────────────────────────────────────────────────────────────
  'import.title': 'Import your old tab graveyards',
  'import.drop.title': 'Drop your export file here',
  'import.drop.body': 'OneTab, Session Buddy or bookmarks HTML —',
  'import.drop.browse': 'browse files',
  'import.readyEyebrow': 'READY TO IMPORT',

  // ── settings ───────────────────────────────────────────────────────────
  'settings.title': 'Settings',
  'settings.section.account': 'ACCOUNT & SYNC',
  'settings.section.browsers': 'CONNECTED BROWSERS',
  'settings.section.capture': 'CAPTURE RULES',
  'settings.section.sharing': 'SHARING',
  'settings.section.storage': 'STORAGE',
  'settings.section.appearance': 'APPEARANCE',
  'settings.section.language': 'LANGUAGE',
  'settings.section.about': 'ABOUT',
  'settings.section.danger': 'DANGER ZONE',
  'settings.account.private': 'PRIVATE & SECURE',
  'settings.account.yourAccount': 'Your account',
  'settings.account.proPlan': 'PRO PLAN',
  'settings.account.freePlan': 'FREE PLAN',
  'settings.account.managePlan': 'Manage plan',
  'settings.account.seePro': 'See Pro',
  'settings.account.privacyNote':
    'Your saved pages stay private — only you can see them. We keep them safe and out of everyone else’s reach.',
  'settings.account.billing': 'Billing & payments',
  'settings.browsers.syncAuto': 'Others sync automatically',
  'settings.browsers.thisBrowser': 'This browser',
  'settings.browsers.webApp': 'WEB APP',
  'settings.browsers.activeNow': 'ACTIVE NOW',
  'settings.browsers.installHint':
    'Install the extension in your other browsers — they show up here and sync automatically.',
  'settings.capture.onDevice': 'ON THIS DEVICE',
  'settings.capture.saveContent': 'Save page content for search',
  'settings.capture.saveContentDesc': 'Text only — stored privately for full-text search',
  'settings.capture.skipIncognito': 'Skip incognito windows',
  'settings.capture.neverOn': 'Never capture content on:',
  'settings.capture.addSite': '+ add site',
  'settings.capture.localNote':
    'These rules live in this browser for now — syncing them across devices is coming soon.',
  'settings.sharing.readOnly': 'Read-only public links',
  'settings.sharing.collection': 'Collection',
  'settings.sharing.sweep': 'Sweep',
  'settings.sharing.selectCollection': 'Select a collection…',
  'settings.sharing.selectSweep': 'Select a sweep…',
  'settings.sharing.createLink': 'Create link',
  'settings.sharing.empty.title': 'No share links yet',
  'settings.sharing.empty.body': 'Create a public link for a collection or sweep to share it, read only.',
  'settings.sharing.copyLink': 'Copy link',
  'settings.sharing.revoke': 'Revoke',
  'settings.toast.linkCreated': 'Share link created',
  'settings.toast.linkCreatedBody': 'Anyone with the link can view it — read only.',
  'settings.toast.linkFailed': 'Could not create link',
  'settings.toast.linkFailedBody': 'Please try again in a moment.',
  'settings.toast.linkRevoked': 'Link revoked',
  'settings.toast.linkRevokedBody': 'That link no longer opens.',
  'settings.toast.revokeFailed': 'Could not revoke link',
  'settings.toast.revokeFailedBody': 'Please try again.',
  'settings.toast.copied': 'Copied',
  'settings.toast.copiedBody': 'Share link on your clipboard.',
  'settings.toast.deleteFailed': 'Delete failed',
  'settings.toast.deleteFailedBody': 'We couldn’t delete the account. Try again.',
  'settings.danger.export': 'Export all data',
  'settings.danger.deleteAccount': 'Delete account & data',
  'settings.danger.note': 'Export first — deletion is permanent after 14 days.',
  'settings.storage.saved': '{count} saved',
  'settings.storage.tabs': 'TABS',
  'settings.storage.sessions': 'SESSIONS',
  'settings.storage.indexed': 'INDEXED',
  'settings.appearance.dark': 'Dark theme',
  'settings.appearance.darkDesc': 'Deep navy — the default look',
  'settings.language.label': 'Display language',
  'settings.language.desc': 'Applies immediately, everywhere in the app.',
  'settings.promo.title': 'Get the extension',
  'settings.promo.body': 'Sweep a whole window with one click, straight from your toolbar.',
  'settings.about.webApp': 'Web app',
  'settings.about.extension': 'Extension',
  'settings.deleteModal.title': 'Delete account & data',
  'settings.deleteModal.subtitle': 'This removes your vault everywhere.',
  'settings.deleteModal.body':
    'Every sweep, saved page and collection will be scheduled for deletion. It stays recoverable for 14 days, then it’s gone for good. Export your data first if you might want it back.',
  'settings.deleteModal.keep': 'Keep account',
  'settings.deleteModal.confirm': 'Delete everything',
  'settings.planModal.title': 'Manage your Pro plan',
  'settings.planModal.subtitle': 'Free stays generous — Pro connects everything.',
  'settings.planModal.body':
    'Cancel anytime and you keep Pro until the end of your billing period. After that: local saves stay unlimited, sync drops to one browser, and AI search and collections pause.',
  'settings.planModal.nothingDeleted': 'Nothing is deleted.',
  'settings.planModal.emailSupport': 'Email support to cancel',
  'settings.planModal.keepPro': 'Keep Pro',

  // ── login / verify ─────────────────────────────────────────────────────
  'login.welcome': 'Welcome to Nomad Tabs',
  'login.tagline': 'One vault for every browser you use.',
  'login.emailPlaceholder': 'you@email.com',
  'login.sendLink': 'Send magic link',
  'login.error.tooMany': 'Too many requests — give it a minute and try again.',
  'login.error.sendFailed': 'We couldn’t send the link just now. Try again in a moment.',
  'login.reassure': 'Your saved pages stay private — only you can open your vault.',
  'login.checkInbox': 'Check your inbox',
  'login.sentTo': 'We sent a sign-in link to',
  'login.expires': 'LINK EXPIRES IN 15 MIN',
  'login.nothingThere': 'Nothing there?',
  'login.checkSpam': 'check spam',
  'login.wrongAddress': '← Wrong address? Go back',
  'login.devDivider': 'DEV · PASTE TOKEN',
  'login.tokenPlaceholder': 'token from the API console',
  'verify.signingIn': 'Signing you in…',
  'verify.hangTight': 'Hang tight, this only takes a second.',
  'verify.expired.title': 'This link has expired',
  'verify.expired.body':
    'Magic links are good for 15 minutes and a single use. Request a fresh one and you’ll be right in.',
  'verify.backToSignIn': 'Back to sign in',

  // ── onboarding ─────────────────────────────────────────────────────────
  'onboarding.hero.title1': 'Your browser finally',
  'onboarding.hero.title2': 'has a memory.',
  'onboarding.hero.body':
    'Sweep whole windows into your vault, close them without fear, and find any page again — by what was written on it.',
  'onboarding.getStarted': 'Get started',
  'onboarding.hero.micro': 'TAKES 60 SECONDS · NO ACCOUNT REQUIRED TO EXPLORE',
  'onboarding.perm.title': 'Why your browser asks for access',
  'onboarding.perm.lede': 'The next prompt sounds scary. Here’s what it actually means.',
  'onboarding.perm.head': '“READ AND CHANGE ALL YOUR DATA ON ALL WEBSITES” — TRANSLATED',
  'onboarding.perm.p1.title': 'We read pages only to make them searchable',
  'onboarding.perm.p1.body': 'So you can find “that article about mortgage rates” later.',
  'onboarding.perm.p2.title': 'Your pages stay private on your device',
  'onboarding.perm.p2.body': 'Only you can open your vault — we can’t see what you save.',
  'onboarding.perm.p3.title': 'Banks, email and private sites can be excluded',
  'onboarding.perm.p3.body': 'One allowlist in Settings — content is never captured there.',
  'onboarding.perm.continue': 'Sounds fair — continue',
  'onboarding.perm.later': 'Ask me later',
  'onboarding.perm.micro': 'YOU CAN CHANGE THIS ANYTIME IN SETTINGS',
  'onboarding.rescue.title': 'Rescue your tabs right now',
  'onboarding.rescue.lede': 'Pick one — both take under a minute, and nothing gets lost.',
  'onboarding.rescue.onetab.title': 'Import from OneTab',
  'onboarding.rescue.onetab.body':
    'Bring your whole OneTab graveyard over in one click — groups become sessions.',
  'onboarding.rescue.chooseFile': 'Choose file',
  'onboarding.rescue.onetab.foot': 'ALSO: SESSION BUDDY · BOOKMARKS HTML',
  'onboarding.rescue.ext.title': 'Get the browser extension',
  'onboarding.rescue.ext.body':
    'Sweep a whole window with one click, right from your toolbar. Watch your RAM come back.',
  'onboarding.rescue.ext.foot': 'CHROME · EDGE · YANDEX · COMET',
  'onboarding.rescue.openApp': 'Open the web app →',

  // ── pricing (Free vs Pro) ──────────────────────────────────────────────
  'pricing.title': 'Go Pro when you’re ready',
  'pricing.subtitle': 'Free stays generous. Pro connects everything.',
  'pricing.monthly': 'Monthly ${price}',
  'pricing.yearly': 'Yearly ${price}',
  'pricing.forever': 'FOREVER',
  'pricing.yourPlan': 'YOUR PLAN',
  'pricing.mostLoved': 'MOST LOVED',
  'pricing.perMonth': '/MO',
  'pricing.perMonthYearly': '/MO · BILLED ${total} YEARLY',
  'pricing.free.f1': 'Unlimited local saves',
  'pricing.free.f2': '1 browser synced to cloud',
  'pricing.free.f3': 'Title & URL search',
  'pricing.free.f4': 'Full-text & AI search',
  'pricing.free.f5': 'AI collections',
  'pricing.pro.f1': 'All browsers, all devices synced',
  'pricing.pro.f2': 'Full-text + AI semantic search',
  'pricing.pro.f3': 'AI collections that sort themselves',
  'pricing.pro.f4': 'Version history of saved pages',
  'pricing.pro.f5': 'Priority support',
  'pricing.stayFree': 'Stay on Free',
  'pricing.youreOnPro': 'You’re on Pro',
  'pricing.signInToUpgrade': 'Sign in to upgrade',
  'pricing.checkoutSoon': 'Checkout is opening soon — hang tight, we’ll email you the moment it’s live.',
  'pricing.checkoutFailed': 'Something went wrong starting checkout. Please try again.',
  'pricing.footer': 'CANCEL ANYTIME · EXPORT YOUR DATA WHENEVER · LOCAL SAVES ARE NEVER LOCKED',

  // ── billing / version history ──────────────────────────────────────────
  'billing.title': 'Billing & payments',
  'versions.title': 'Version history',
  'versions.current': 'CURRENT',
  'versions.restoreThis': 'Restore this version',

  // ── trash ──────────────────────────────────────────────────────────────
  'trash.title': 'Trash',
  'trash.autoPurge': 'AUTO-PURGE AFTER 14 DAYS',
  'trash.itemsLoading': '… ITEMS',
  'trash.restoreSelected': 'Restore selected ({count})',
  'trash.restoring': 'Restoring…',
  'trash.emptyTrash': 'Empty trash',
  'trash.error.title': 'Couldn’t load trash',
  'trash.emptyState.title': 'Trash is empty',
  'trash.emptyState.body':
    'Deleted sessions and pages wait here for 14 days, then they’re purged automatically.',
  'trash.footNote': 'DELETING FROM TRASH IS PERMANENT · EXPORT ALL DATA ANYTIME IN SETTINGS → DANGER ZONE',
  'trash.purge.title': 'Delete forever?',
  'trash.purge.subtitle': '“{title}” will be permanently removed — no 14-day recovery.',
  'trash.purge.confirm': 'Delete forever',
  'trash.purge.sweepBody': 'This deletes the session and its {tabs} for good.',
  'trash.purge.pageBody': 'This deletes the saved page and its captured content for good.',
  'trash.emptyConfirm.title': 'Empty trash?',
  'trash.emptyConfirm.subtitle': 'Permanently deletes {entries}.',
  'trash.emptyConfirm.body':
    'Everything in trash is gone for good — this can’t be undone. You can export all your data anytime in Settings → Danger zone.',
  'trash.toast.restored': 'Restored',
  'trash.toast.restoredBody': '“{title}” is back in your vault.',
  'trash.toast.restoredMany': 'Restored {entries}',
  'trash.toast.restoreFailed': 'Could not restore',
  'trash.toast.restoreManyFailed': 'Could not restore selection',
  'trash.toast.purged': 'Deleted forever',
  'trash.toast.purgedBody': '“{title}” is gone for good.',
  'trash.toast.purgeFailed': 'Could not delete',
  'trash.toast.emptied': 'Trash emptied',
  'trash.toast.emptiedBody': 'Everything in trash was permanently deleted.',
  'trash.toast.emptyFailed': 'Could not empty trash',
} as const;

/** Every valid message key — derived from the EN catalog. */
export type MessageKey = keyof typeof en;

/** The contract each locale file must fulfil (all keys, string values). */
export type Messages = Record<MessageKey, string>;
